import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Car, MapPin, Shield, CalendarDays, DollarSign, ScanLine, CarFront } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Card, Select, EmptyState, Skeleton } from '@/components/ui'
import { useProperties } from '@/hooks/useProperties'
import { useSelectedPropertyStore } from '@/stores'
import { useAccountSettings } from '@/hooks/useAccountSettings'

export function ParkingIndexPage() {
  const { t } = useTranslation('parking')
  const { t: tc } = useTranslation('common')
  const { data: propertiesData, isLoading } = useProperties()
  const properties = propertiesData?.data ?? []

  // Property scope is shared with the global header switcher. Header.tsx now
  // owns validating the persisted id against the account's property list and
  // resets it to null once it's gone stale (D3), so by the time this page
  // renders, a stale id has already been cleared, so no local validation here.
  const selectedPropertyId = useSelectedPropertyStore((s) => s.selectedPropertyId)
  const setSelectedPropertyId = useSelectedPropertyStore((s) => s.setSelectedPropertyId)

  // QA B-048 — gate the LPR + parking-fee links on their account settings
  // (single-gate, admin-managed, EP-53).
  const { data: accountSettings } = useAccountSettings()
  const lprEnabled = accountSettings?.settings?.lpr_enabled ?? false
  const parkingFeeEnabled = accountSettings?.settings?.parking_fee_enabled ?? false

  const parkingLinks = (propertyId: number) => [
    // EP-161: the day's kiosk sessions come first; they are the parking fee module.
    ...(parkingFeeEnabled
      ? [{ to: `/properties/${propertyId}/parking/sessions`, icon: <CarFront className="w-5 h-5" />, label: t('sessions.title') }]
      : []),
    { to: `/properties/${propertyId}/parking/zones`, icon: <MapPin className="w-5 h-5" />, label: t('zones.title') },
    { to: `/properties/${propertyId}/parking/vehicles`, icon: <Car className="w-5 h-5" />, label: t('vehicles.title') },
    { to: `/properties/${propertyId}/parking/permits`, icon: <Shield className="w-5 h-5" />, label: t('permits.title') },
    { to: `/properties/${propertyId}/parking/daily`, icon: <CalendarDays className="w-5 h-5" />, label: t('daily_logs.title') },
    ...(lprEnabled
      ? [{ to: `/properties/${propertyId}/parking/lpr`, icon: <ScanLine className="w-5 h-5" />, label: t('lpr.title') }]
      : []),
    ...(parkingFeeEnabled
      ? [{ to: `/properties/${propertyId}/parking-fees`, icon: <DollarSign className="w-5 h-5" />, label: t('fee_configs.title'), admin: true }]
      : []),
  ]

  const propertyOptions = [
    { value: '', label: t('list_page.select_property') },
    ...properties.map((p) => ({ value: String(p.id), label: p.name })),
  ]

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: tc('nav.parking') }]} />
      <PageHeader
        title={tc('nav.parking')}
        titleAction={
          <Select
            data-testid="page-property-picker-mobile"
            aria-label={t('list_page.select_property')}
            value={selectedPropertyId ? String(selectedPropertyId) : ''}
            onChange={(e) => setSelectedPropertyId(e.target.value ? Number(e.target.value) : null)}
            options={propertyOptions}
            className="max-w-[9rem]"
            size="sm"
          />
        }
      />

      {/* Property scope is the single top-right picker (header switcher on
          desktop, PageHeader titleAction on mobile) — no redundant in-body card. */}

      {/* Parking sections */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      ) : !selectedPropertyId ? (
        <Card>
          <EmptyState
            title={t('list_page.no_property_selected')}
            description={tc('labels.select_property_in_header')}
          />
        </Card>
      ) : (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">{t('list_page.parking_modules')}</h3>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {parkingLinks(selectedPropertyId).map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="flex items-center gap-3 p-4 border border-base-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-base-200 flex items-center justify-center">
                  {link.icon}
                </div>
                <span className="font-medium">{link.label}</span>
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}
